
import React, { useState, useEffect } from 'react';
import { X, Calculator, Filter, Scissors, Save, ArrowLeft, ArrowRight, Plus, Trash2, Info, CheckCircle2 } from 'lucide-react';

const isProd = process.env.NODE_ENV === 'production';
const API_BASE = process.env.REACT_APP_API_BASE || (isProd ? '' : 'http://localhost:4000/api');

const DataTransformModal = ({ isOpen, onClose, tables, onSaveDataset }) => {
    const [step, setStep] = useState(1); // 1: Select Source & Tools, 2: Preview & Save
    const [sourceTable, setSourceTable] = useState('');
    const [transformSteps, setTransformSteps] = useState([]);
    const [previewData, setPreviewData] = useState([]);
    const [generatedSql, setGeneratedSql] = useState('');
    const [newDatasetName, setNewDatasetName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [columns, setColumns] = useState([]);

    // Reset when modal opens
    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setSourceTable('');
            setTransformSteps([]);
            setPreviewData([]);
            setGeneratedSql('');
            setError('');
            setNewDatasetName('');
        }
    }, [isOpen]);

    // Update columns when sourceTable changes
    useEffect(() => {
        if (sourceTable) {
            const table = tables.find(t => t.name === sourceTable);
            setColumns(table ? table.columns : []);
        } else {
            setColumns([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sourceTable]);

    const addStep = (type) => {
        let newStep = { id: Date.now(), type };
        if (type === 'formula') {
            newStep = { ...newStep, outputField: 'NewField', formula: '[Column] * 1' };
        } else if (type === 'filter') {
            newStep = { ...newStep, column: columns[0] || '', operator: '=', value: '' };
        } else if (type === 'sample') {
            newStep = { ...newStep, method: 'first_n', count: 100 };
        }
        setTransformSteps([...transformSteps, newStep]);
    };

    const removeStep = (id) => {
        setTransformSteps(transformSteps.filter(s => s.id !== id));
    };

    const updateStep = (id, updates) => {
        setTransformSteps(transformSteps.map(s => s.id === id ? { ...s, ...updates } : s));
    };

    const handlePreview = async () => {
        if (!sourceTable) {
            setError('Please select a source table.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_BASE}/data/preview-transform`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    sourceTable,
                    steps: transformSteps
                })
            });

            const data = await res.json();
            if (res.ok) {
                setPreviewData(data.preview);
                setGeneratedSql(data.sql);
                setStep(2);
            } else {
                setError(data.error || 'Failed to preview transformation');
            }
        } catch (err) {
            setError('Network error: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!newDatasetName.trim()) {
            setError('Please enter a name for the new dataset.');
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE}/data/create-view`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    viewName: newDatasetName,
                    sql: generatedSql
                })
            });

            const data = await res.json();
            if (res.ok) {
                onSaveDataset && onSaveDataset();
                onClose();
            } else {
                setError(data.error || 'Failed to create dataset');
            }
        } catch (err) {
            setError('Network error: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="style-modal-overlay">
            <div className="style-modal-content" style={{ maxWidth: '900px', width: '95%', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
                <div className="style-modal-header" style={{ padding: '20px', borderBottom: '1px solid var(--glass-border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div className="icon-container-premium">
                            <Scissors size={20} />
                        </div>
                        <div>
                            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Data Transformation</h2>
                            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-dim)' }}>Apply formulas, filters, and sampling</p>
                        </div>
                    </div>
                    <button className="close-btn" onClick={onClose}><X size={20} /></button>
                </div>

                <div style={{ flex: 1, overflowY: 'auto', padding: '24px' }}>
                    {error && (
                        <div style={{ padding: '12px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', borderRadius: '8px', marginBottom: '20px', border: '1px solid rgba(239, 68, 68, 0.2)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Info size={16} />
                            {error}
                        </div>
                    )}

                    {step === 1 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            <div className="form-group">
                                <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-dim)', marginBottom: '8px', display: 'block' }}>Select Source Dataset</label>
                                <select
                                    className="premium-input"
                                    value={sourceTable}
                                    onChange={(e) => setSourceTable(e.target.value)}
                                >
                                    <option value="">Select Table...</option>
                                    {tables.map(t => <option key={t.name} value={t.name}>{t.name}</option>)}
                                </select>
                            </div>

                            <div style={{ background: 'var(--bg-tertiary)', borderRadius: '12px', padding: '20px', border: '1px solid var(--glass-border)' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                    <h3 style={{ margin: 0, fontSize: '1rem' }}>Transformation Steps</h3>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                        <button onClick={() => addStep('formula')} className="btn-action-premium" style={{ padding: '6px 12px', fontSize: '0.8rem' }} disabled={!sourceTable}>
                                            <Calculator size={14} style={{ marginRight: '6px' }} /> Formula
                                        </button>
                                        <button onClick={() => addStep('filter')} className="btn-action-premium" style={{ padding: '6px 12px', fontSize: '0.8rem' }} disabled={!sourceTable}>
                                            <Filter size={14} style={{ marginRight: '6px' }} /> Filter
                                        </button>
                                        <button onClick={() => addStep('sample')} className="btn-action-premium" style={{ padding: '6px 12px', fontSize: '0.8rem' }} disabled={!sourceTable}>
                                            <Scissors size={14} style={{ marginRight: '6px' }} /> Sample
                                        </button>
                                    </div>
                                </div>

                                {transformSteps.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-dim)', border: '2px dashed var(--glass-border)', borderRadius: '12px' }}>
                                        <p>No steps added yet. Start by adding a Formula, Filter, or Sample tool.</p>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                        {transformSteps.map((step, idx) => (
                                            <div key={step.id} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '16px', background: 'var(--bg-secondary)', borderRadius: '10px', border: '1px solid var(--glass-border)' }}>
                                                <div style={{ width: '24px', height: '24px', background: 'var(--accent-primary)', color: 'white', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                                    {idx + 1}
                                                </div>

                                                <div style={{ flex: 1 }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                                                        <span style={{ fontWeight: 600, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                                            {step.type} Tool
                                                        </span>
                                                        <button onClick={() => removeStep(step.id)} style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: '2px' }}>
                                                            <Trash2 size={16} />
                                                        </button>
                                                    </div>

                                                    {step.type === 'formula' && (
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '12px' }}>
                                                            <div>
                                                                <label style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Output Field</label>
                                                                <input
                                                                    type="text"
                                                                    className="premium-input-small"
                                                                    value={step.outputField}
                                                                    onChange={e => updateStep(step.id, { outputField: e.target.value })}
                                                                />
                                                            </div>
                                                            <div>
                                                                <label style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Formula (Use [Field])</label>
                                                                <input
                                                                    type="text"
                                                                    className="premium-input-small"
                                                                    value={step.formula}
                                                                    onChange={e => updateStep(step.id, { formula: e.target.value })}
                                                                    placeholder="e.g. [Price] * 1.1"
                                                                />
                                                            </div>
                                                        </div>
                                                    )}

                                                    {step.type === 'filter' && (
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                                                            <div>
                                                                <label style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Column</label>
                                                                <select
                                                                    className="premium-input-small"
                                                                    value={step.column}
                                                                    onChange={e => updateStep(step.id, { column: e.target.value })}
                                                                >
                                                                    {columns.map(c => <option key={c} value={c}>{c}</option>)}
                                                                </select>
                                                            </div>
                                                            <div>
                                                                <label style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Operator</label>
                                                                <select
                                                                    className="premium-input-small"
                                                                    value={step.operator}
                                                                    onChange={e => updateStep(step.id, { operator: e.target.value })}
                                                                >
                                                                    <option value="=">=</option>
                                                                    <option value="!=">!=</option>
                                                                    <option value=">">&gt;</option>
                                                                    <option value="<">&lt;</option>
                                                                    <option value=">=">&gt;=</option>
                                                                    <option value="<=">&lt;=</option>
                                                                    <option value="LIKE">Contains</option>
                                                                </select>
                                                            </div>
                                                            <div>
                                                                <label style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Value</label>
                                                                <input
                                                                    type="text"
                                                                    className="premium-input-small"
                                                                    value={step.value}
                                                                    onChange={e => updateStep(step.id, { value: e.target.value })}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}

                                                    {step.type === 'sample' && (
                                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                                                            <div>
                                                                <label style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Method</label>
                                                                <select
                                                                    className="premium-input-small"
                                                                    value={step.method}
                                                                    onChange={e => updateStep(step.id, { method: e.target.value })}
                                                                >
                                                                    <option value="first_n">First N Records</option>
                                                                    <option value="random">Random Subset</option>
                                                                </select>
                                                            </div>
                                                            <div>
                                                                <label style={{ fontSize: '0.75rem', color: 'var(--text-dim)' }}>Count</label>
                                                                <input
                                                                    type="number"
                                                                    className="premium-input-small"
                                                                    value={step.count}
                                                                    onChange={e => updateStep(step.id, { count: e.target.value })}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            <div className="form-group">
                                <label style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-dim)', marginBottom: '8px', display: 'block' }}>New Dataset Name</label>
                                <input
                                    type="text"
                                    className="premium-input"
                                    placeholder="e.g. transformed_sales_data"
                                    value={newDatasetName}
                                    onChange={e => setNewDatasetName(e.target.value)}
                                />
                                <p style={{ fontSize: '0.75rem', color: 'var(--text-dim)', marginTop: '6px' }}>This will create a virtual table (view) in your database.</p>
                            </div>

                            <div style={{ border: '1px solid var(--glass-border)', borderRadius: '12px', overflow: 'hidden' }}>
                                <div style={{ background: 'var(--bg-tertiary)', padding: '12px 16px', borderBottom: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <CheckCircle2 size={16} style={{ color: 'var(--accent-primary)' }} />
                                    <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>Preview Result (First 10 Rows)</span>
                                </div>
                                <div style={{ overflowX: 'auto', maxHeight: '300px' }}>
                                    {previewData.length > 0 ? (
                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                            <thead>
                                                <tr style={{ background: 'rgba(255,255,255,0.02)', textAlign: 'left' }}>
                                                    {Object.keys(previewData[0]).map(key => (
                                                        <th key={key} style={{ padding: '12px', borderBottom: '1px solid var(--glass-border)', color: 'var(--text-dim)', fontWeight: 600 }}>{key}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {previewData.map((row, i) => (
                                                    <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.02)' }}>
                                                        {Object.values(row).map((val, j) => (
                                                            <td key={j} style={{ padding: '12px', whiteSpace: 'nowrap' }}>{String(val)}</td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    ) : (
                                        <div style={{ padding: '20px', textAlign: 'center', color: 'var(--text-dim)' }}>No data to preview</div>
                                    )}
                                </div>
                            </div>

                            <div style={{ background: 'var(--bg-tertiary/20)', padding: '16px', borderRadius: '10px', border: '1px dashed var(--glass-border)' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', color: 'var(--text-dim)', fontSize: '0.85rem' }}>
                                    <Info size={14} />
                                    <span>SQL Preview</span>
                                </div>
                                <code style={{ fontSize: '0.75rem', display: 'block', wordBreak: 'break-all', opacity: 0.7 }}>
                                    {generatedSql}
                                </code>
                            </div>
                        </div>
                    )}
                </div>

                <div className="style-modal-footer" style={{ padding: '20px', borderTop: '1px solid var(--glass-border)', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    {step === 2 && (
                        <button className="btn-secondary-premium" onClick={() => setStep(1)} disabled={loading}>
                            <ArrowLeft size={16} style={{ marginRight: '8px' }} /> Back
                        </button>
                    )}

                    <button
                        className="btn-action-premium"
                        onClick={step === 1 ? handlePreview : handleSave}
                        disabled={loading || (step === 1 && transformSteps.length === 0)}
                    >
                        {loading ? 'Processing...' : (
                            step === 1 ? (
                                <><ArrowRight size={16} style={{ marginRight: '8px' }} /> Next: Preview</>
                            ) : (
                                <><Save size={16} style={{ marginRight: '8px' }} /> Create Dataset</>
                            )
                        )}
                    </button>
                </div>
            </div>

            <style>{`
                .premium-input-small {
                    width: 100%;
                    padding: 8px 10px;
                    background: var(--bg-primary);
                    border: 1px solid var(--glass-border);
                    border-radius: 6px;
                    color: var(--text-primary);
                    font-size: 0.8rem;
                    outline: none;
                    transition: all 0.2s;
                }
                .premium-input-small:focus {
                    border-color: var(--accent-primary);
                    box-shadow: 0 0 0 2px rgba(139, 92, 246, 0.2);
                }
            `}</style>
        </div>
    );
};

export default DataTransformModal;
